import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Activity,
  BadgeCheck,
  Bell,
  ChevronRight,
  CreditCard,
  HelpCircle,
  PlusCircle,
  TrendingUp,
  Video,
} from "lucide-react";
import { notifications } from "@mantine/notifications";
import { imgAvatar } from "../../../assets";
import { Endpoints } from "../../../lib/endpoints";
import { PerformRequest } from "../../../lib/usePerformRequest";
import { InlineLoader } from "../../../components/Loader/LoadingOverlay";

interface DashboardStats {
  earnings_this_month: number;
  earnings_delta: number;
  active_transactions: number;
  issued_certificates: number;
  pending_payments: number;
  user_name?: string;
  first_name?: string;
  last_name?: string;
}

interface Transaction {
  id: string;
  property_type: string;
  units?: string;
  client_name: string;
  status: "PAID" | "PENDING" | "PROCESSING";
  date: string;
  time: string;
  reference: string;
}

interface DashboardResponse {
  stats: DashboardStats;
  recent_transactions: Transaction[];
}

function MiniStat({
  title,
  value,
  label,
  icon,
}: {
  title: string;
  value: string;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="dashboard-mini-stat">
      <div className="dashboard-mini-stat__left">
        <div className="dashboard-mini-stat__title">{title}</div>
        <div className="dashboard-mini-stat__value">{value}</div>
        <div className="dashboard-mini-stat__label">{label}</div>
      </div>
      <div className="dashboard-mini-stat__icon" aria-hidden="true">
        {icon}
      </div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    earnings_this_month: 0,
    earnings_delta: 0,
    active_transactions: 0,
    issued_certificates: 0,
    pending_payments: 0,
  });
  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>(
    []
  );
  const [greetingName, setGreetingName] = useState("Barrister");

  useEffect(() => {
    async function fetchDashboardData() {
      setIsLoading(true);
      try {
        // Fetch stats
        const statsEndpoint = Endpoints.GetDashboardStats();
        const statsResponse = await PerformRequest<DashboardStats>({
          method: statsEndpoint.method,
          route: statsEndpoint.route,
        });

        if (statsResponse.status === 200 && statsResponse.data) {
          // Ensure all stats fields have valid numbers
          setStats({
            earnings_this_month: statsResponse.data.earnings_this_month ?? 0,
            earnings_delta: statsResponse.data.earnings_delta ?? 0,
            active_transactions: statsResponse.data.active_transactions ?? 0,
            issued_certificates: statsResponse.data.issued_certificates ?? 0,
            pending_payments: statsResponse.data.pending_payments ?? 0,
            user_name: statsResponse.data.user_name,
            first_name: statsResponse.data.first_name,
            last_name: statsResponse.data.last_name,
          });
          if (statsResponse.data.user_name) {
            setGreetingName(statsResponse.data.user_name);
          } else if (statsResponse.data.first_name) {
            setGreetingName(
              `Barrister ${statsResponse.data.first_name} ${
                statsResponse.data.last_name || ""
              }`.trim()
            );
          }
        }

        // Fetch recent transactions
        const transactionsEndpoint = Endpoints.GetTransactions({ page: 1 });
        const transactionsResponse = await PerformRequest<{
          results: Transaction[];
          count: number;
        }>({
          method: transactionsEndpoint.method,
          route: transactionsEndpoint.route,
        });

        if (transactionsResponse.status === 200 && transactionsResponse.data) {
          const transactions = Array.isArray(transactionsResponse.data)
            ? transactionsResponse.data
            : transactionsResponse.data.results || [];
          setRecentTransactions(transactions.slice(0, 3));
        }
      } catch (error: any) {
        notifications.show({
          title: "Error",
          message: "Failed to load dashboard data. Please refresh the page.",
          color: "red",
        });
      } finally {
        setIsLoading(false);
      }
    }

    fetchDashboardData();
  }, []);

  return (
    <div className="dashboard-home">
      <div className="dashboard-home__header">
        <div className="dashboard-home__greeting">
          <div className="dashboard-home__greeting-line">Good Morning,</div>
          <div className="dashboard-home__greeting-name">{greetingName}</div>
        </div>

        <div className="dashboard-home__header-actions">
          <img className="dashboard-home__avatar" src={imgAvatar} alt="" />
          <button
            className="dashboard-home__icon-button"
            type="button"
            aria-label="Notifications"
          >
            <Bell size={22} />
          </button>
        </div>
      </div>

      {isLoading ? (
        <InlineLoader isLoading={true} text="Loading dashboard..." />
      ) : (
        <>
          <section
            className="dashboard-earnings"
            aria-label="Earnings this month"
          >
            <div className="dashboard-earnings__left">
              <div className="dashboard-earnings__title">
                EARNINGS THIS MONTH
              </div>
              <div className="dashboard-earnings__amount">
                ₦
                {(stats.earnings_this_month ?? 0).toLocaleString("en-NG", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
              <div className="dashboard-earnings__delta">
                {(stats.earnings_delta ?? 0) >= 0 ? "+" : ""}
                {stats.earnings_delta ?? 0}% from last month
              </div>
            </div>
            <div className="dashboard-earnings__icon" aria-hidden="true">
              <TrendingUp size={34} />
            </div>
          </section>

          <div className="dashboard-mini-stats" aria-label="Summary">
            <MiniStat
              title="ACTIVE"
              value={(stats.active_transactions ?? 0).toString()}
              label="Transaction"
              icon={<Activity size={22} />}
            />
            <MiniStat
              title="ISSUED"
              value={(stats.issued_certificates ?? 0).toString()}
              label="Certificate"
              icon={<BadgeCheck size={22} />}
            />
            <MiniStat
              title="PENDING"
              value={(stats.pending_payments ?? 0).toString()}
              label="Payment"
              icon={<CreditCard size={22} />}
            />
          </div>
        </>
      )}

      <button
        className="dashboard-cta"
        type="button"
        onClick={() => navigate("/d/new-transaction")}
      >
        <PlusCircle size={18} aria-hidden="true" />
        <span>Start New Transaction</span>
      </button>

      <section className="dashboard-section">
        <div className="dashboard-section__header">
          <h2 className="dashboard-section__title">Recent Transactions</h2>
          <Link className="dashboard-section__link" to="/d/transactions">
            View All
          </Link>
        </div>

        <div className="dashboard-transactions">
          {recentTransactions.length > 0 ? (
            recentTransactions.map((tx) => {
              const statusTone =
                tx.status === "PAID"
                  ? "success"
                  : tx.status === "PROCESSING"
                  ? "processing"
                  : "pending";
              return (
                <div className="dashboard-tx" key={tx.reference + tx.id}>
                  <div className="dashboard-tx__property">
                    <div className="dashboard-tx__property-name">
                      {tx.property_type}
                    </div>
                    {tx.units ? (
                      <div className="dashboard-tx__property-units">
                        {tx.units}
                      </div>
                    ) : null}
                  </div>

                  <div className="dashboard-tx__name">{tx.client_name}</div>

                  <div
                    className={
                      statusTone === "success"
                        ? "dashboard-pill dashboard-pill--success"
                        : statusTone === "processing"
                        ? "dashboard-pill dashboard-pill--processing"
                        : "dashboard-pill dashboard-pill--pending"
                    }
                  >
                    {tx.status}
                  </div>

                  <div className="dashboard-tx__datetime">
                    <div className="dashboard-tx__date">{tx.date}</div>
                    <div className="dashboard-tx__time">{tx.time}</div>
                  </div>

                  <div className="dashboard-tx__ref">{tx.reference}</div>
                </div>
              );
            })
          ) : (
            <div className="dashboard-empty">No recent transactions</div>
          )}
        </div>
      </section>

      <section className="dashboard-section" aria-label="Training and support">
        <h2 className="dashboard-section__title dashboard-section__title--large">
          Training &amp; Support
        </h2>

        <div className="dashboard-support">
          <button className="dashboard-support-card" type="button">
            <span className="dashboard-support-card__icon" aria-hidden="true">
              <Video size={20} />
            </span>
            <span className="dashboard-support-card__text">
              <span className="dashboard-support-card__title">
                Video Tutorials
              </span>
              <span className="dashboard-support-card__subtitle">
                Learn how to use the platform
              </span>
            </span>
            <ChevronRight size={20} aria-hidden="true" />
          </button>

          <button
            className="dashboard-support-card"
            type="button"
            onClick={() => navigate("/d/support")}
          >
            <span
              className="dashboard-support-card__icon dashboard-support-card__icon--blue"
              aria-hidden="true"
            >
              <HelpCircle size={20} />
            </span>
            <span className="dashboard-support-card__text">
              <span className="dashboard-support-card__title">
                Help &amp; Contact
              </span>
              <span className="dashboard-support-card__subtitle">
                Get assistance from our team
              </span>
            </span>
            <ChevronRight size={20} aria-hidden="true" />
          </button>
        </div>
      </section>
    </div>
  );
}
